import * as v from 'valibot'
import type { LoroDoc } from 'loro-crdt'

/* Shared vocabulary for the Policy Initiatives pipeline. Pure: no DO, no
   network — the admin app imports it through `@worker/policy-builder/client`
   so the browser derives the checklist, score, and summary with the exact code
   the Durable Objects use. */

/** Room name of the singleton registry agent. */
export const POLICY_INITIATIVES_ROOM = 'default'

export const INITIATIVE_STAGES = [
  'intake',
  'triage',
  'scoping',
  'ready',
  'build',
  'published',
  'deferred',
  'rejected',
] as const
export type InitiativeStage = (typeof INITIATIVE_STAGES)[number]

/** The linear happy path shown as the stepper; deferred/rejected sit outside it. */
export const PIPELINE_STAGES = [
  'intake',
  'triage',
  'scoping',
  'ready',
  'build',
  'published',
] as const satisfies readonly InitiativeStage[]

export const STAGE_LABELS: Record<InitiativeStage, string> = {
  intake: 'Intake',
  triage: 'Triage',
  scoping: 'Scoping',
  ready: 'Ready for build',
  build: 'In build',
  published: 'Published',
  deferred: 'Deferred',
  rejected: 'Rejected',
}

/** Legal stage moves. Anything not listed is refused by the agent. */
export const STAGE_TRANSITIONS: Record<InitiativeStage, InitiativeStage[]> = {
  intake: ['triage', 'rejected'],
  triage: ['scoping', 'deferred', 'rejected'],
  scoping: ['ready', 'deferred'],
  ready: ['build', 'scoping'],
  build: ['published', 'scoping'],
  published: [],
  deferred: ['triage', 'rejected'],
  rejected: ['triage'],
}

/** Primary "advance" action per stage, or null when there is none. */
export const NEXT_STAGE: Record<
  InitiativeStage,
  { stage: InitiativeStage; label: string } | null
> = {
  intake: { stage: 'triage', label: 'Submit to triage' },
  triage: { stage: 'scoping', label: 'Send to scoping' },
  scoping: { stage: 'ready', label: 'Mark scope complete' },
  ready: { stage: 'build', label: 'Hand off to build' },
  build: { stage: 'published', label: 'Mark published' },
  published: null,
  deferred: { stage: 'triage', label: 'Return to triage' },
  rejected: { stage: 'triage', label: 'Reopen' },
}

export const INITIATIVE_STREAMS = ['value', 'compliance'] as const
export type InitiativeStream = (typeof INITIATIVE_STREAMS)[number]
export const STREAM_LABELS: Record<InitiativeStream, string> = {
  value: 'Value generating',
  compliance: 'Compliance',
}

export const INTAKE_SOURCES = [
  'planned-backlog',
  'data-exploration',
  'customer-request',
] as const
export type IntakeSource = (typeof INTAKE_SOURCES)[number]
export const INTAKE_SOURCE_LABELS: Record<IntakeSource, string> = {
  'planned-backlog': 'Planned backlog',
  'data-exploration': 'Data exploration',
  'customer-request': 'Customer request',
}

export const COMPLEXITIES = ['S', 'M', 'L'] as const
export type Complexity = (typeof COMPLEXITIES)[number]

/** Loro container names inside one initiative document. */
export const INITIATIVE_DOC = {
  meta: 'meta',
  scope: 'scope',
  procedureCodes: 'procedureCodes',
  claimTypes: 'claimTypes',
  insightLogic: 'insightLogic',
  tasks: 'tasks',
  activity: 'activity',
} as const

/** Loro container holding the registry (`id → InitiativeSummary`) in the manager. */
export const REGISTRY_CONTAINER = 'initiatives'

/** Scalar intake + estimate fields — `meta` LoroMap. */
export interface InitiativeMeta {
  id: string
  title: string
  stage: InitiativeStage
  stream: InitiativeStream
  intakeSource: IntakeSource
  /* PaAPI ids plus their display names: the label is denormalized so the
     registry, list, and DO render without a PaAPI round trip. */
  connectorId: string
  connector: string
  topicId: string
  topic: string
  owner: string
  description: string
  targetRelease: string
  /** Estimated annual value in USD; null until estimated. */
  estValue: number | null
  claimLines: number | null
  complexity: Complexity | null
  createdAt: string
  stageChangedAt: string
}

/** Scope-record fields — `scope` LoroMap. */
export interface ScopeFields {
  /** PaAPI ReferenceDocument id + its `source` name. */
  referenceDocumentId: string
  referenceSource: string
  excerpt: string
  /** PaAPI EditType id + its `edit_type` name ("edit types AKA logic types"). */
  editTypeId: string
  editType: string
  historyBehavior: string
  modifierHandling: string
}

export interface BuildTask {
  id: string
  title: string
  detail: string
  owner: string
  estimate: string
}

export interface ActivityEvent {
  id: string
  at: string
  actor: string
  text: string
  icon: string
}

/** Plain-object read of one initiative document. */
export interface InitiativeDocView {
  meta: InitiativeMeta
  scope: ScopeFields
  procedureCodes: string[]
  claimTypes: string[]
  insightLogic: string
  tasks: BuildTask[]
  activity: ActivityEvent[]
}

/** What the registry mirrors per initiative; enough for list, pipeline, triage. */
export interface InitiativeSummary {
  id: string
  title: string
  stage: InitiativeStage
  stream: InitiativeStream
  intakeSource: IntakeSource
  connector: string
  topic: string
  owner: string
  referenceSource: string
  estValue: number | null
  claimLines: number | null
  complexity: Complexity | null
  score: number
  scopeComplete: number
  scopeTotal: number
  tasksGenerated: boolean
  createdAt: string
  stageChangedAt: string
  updatedAt: string
}

export interface ScopeChecklistItem {
  key: string
  label: string
  done: boolean
}

export const DEFAULT_META: InitiativeMeta = {
  id: '',
  title: '',
  stage: 'intake',
  stream: 'value',
  intakeSource: 'planned-backlog',
  connectorId: '',
  connector: '',
  topicId: '',
  topic: '',
  owner: '',
  description: '',
  targetRelease: '',
  estValue: null,
  claimLines: null,
  complexity: null,
  createdAt: '',
  stageChangedAt: '',
}

export const DEFAULT_SCOPE: ScopeFields = {
  referenceDocumentId: '',
  referenceSource: '',
  excerpt: '',
  editTypeId: '',
  editType: '',
  historyBehavior: '',
  modifierHandling: '',
}

// --- wire schemas ------------------------------------------------------------

const owner = v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(120))

export const CreateInitiativeSchema = v.object({
  title: v.pipe(v.string(), v.trim(), v.minLength(3), v.maxLength(200)),
  stream: v.picklist(INITIATIVE_STREAMS),
  intakeSource: v.picklist(INTAKE_SOURCES),
  referenceDocumentId: v.optional(v.string(), ''),
  referenceSource: v.optional(
    v.pipe(v.string(), v.trim(), v.maxLength(300)),
    '',
  ),
  topicId: v.optional(v.string(), ''),
  topic: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(120)), ''),
  connectorId: v.optional(v.string(), ''),
  connector: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(120)), ''),
  description: v.optional(v.pipe(v.string(), v.trim(), v.maxLength(4000)), ''),
  /** `intake` saves a draft; `triage` submits straight into the queue. */
  stage: v.optional(v.picklist(['intake', 'triage']), 'triage'),
})
export type CreateInitiativeInput = v.InferOutput<typeof CreateInitiativeSchema>

/**
 * Client → manager messages.
 *
 * There is deliberately no `actor` field: the agent attributes every action
 * to the identity verified during the WebSocket upgrade and bound to the
 * connection, so a socket cannot log a change as somebody else.
 */
export const ManagerMessageSchema = v.variant('type', [
  v.object({
    type: v.literal('create'),
    input: CreateInitiativeSchema,
  }),
  v.object({
    type: v.literal('transition'),
    id: v.string(),
    stage: v.picklist(INITIATIVE_STAGES),
    /**
     * The stage the caller believed the initiative was in. The agent refuses
     * the move if it no longer matches, so two tabs (or a retry) cannot chain
     * two individually-legal transitions into one nobody asked for.
     */
    expectedStage: v.optional(v.picklist(INITIATIVE_STAGES)),
  }),
])
export type ManagerMessage = v.InferOutput<typeof ManagerMessageSchema>

/** Client → initiative messages; attribution is server-side (see above). */
export const InitiativeMessageSchema = v.variant('type', [
  v.object({
    type: v.literal('transition'),
    stage: v.picklist(INITIATIVE_STAGES),
    /** See `ManagerMessageSchema`; guards against a stale view. */
    expectedStage: v.optional(v.picklist(INITIATIVE_STAGES)),
  }),
  v.object({
    type: v.literal('generate-tasks'),
  }),
  v.object({
    type: v.literal('reassign'),
    owner,
  }),
])
export type InitiativeMessage = v.InferOutput<typeof InitiativeMessageSchema>

// --- pure derivations --------------------------------------------------------

export const scopeChecklist = (
  view: Pick<
    InitiativeDocView,
    'scope' | 'procedureCodes' | 'claimTypes' | 'insightLogic'
  >,
): ScopeChecklistItem[] => [
  {
    key: 'referenceSource',
    label: 'Reference source linked',
    done: view.scope.referenceSource.trim().length > 0,
  },
  {
    key: 'excerpt',
    label: 'Excerpt selected',
    done: view.scope.excerpt.trim().length > 0,
  },
  {
    key: 'editType',
    label: 'Edit type set',
    done: view.scope.editType.trim().length > 0,
  },
  {
    key: 'procedureCodes',
    label: 'Procedure codes listed',
    done: view.procedureCodes.length > 0,
  },
  {
    key: 'claimTypes',
    label: 'Claim types set',
    done: view.claimTypes.length > 0,
  },
  {
    key: 'insightLogic',
    label: 'Insight logic written',
    done: view.insightLogic.trim().length > 0,
  },
  {
    key: 'historyBehavior',
    label: 'History behavior',
    done: view.scope.historyBehavior.trim().length > 0,
  },
  {
    key: 'modifierHandling',
    label: 'Modifier handling',
    done: view.scope.modifierHandling.trim().length > 0,
  },
]

/**
 * Prioritization score, 0–100. ponytail: a fixed log-scale heuristic
 * (value + claim volume, minus complexity) so every surface ranks identically;
 * the upgrade path is a tunable scoring config on the manager.
 */
export const scoreInitiative = (
  meta: Pick<InitiativeMeta, 'estValue' | 'claimLines' | 'complexity'>,
): number => {
  const valuePts =
    meta.estValue && meta.estValue > 0
      ? Math.min(50, Math.round(12 * Math.log10(meta.estValue / 1000 + 1)))
      : 0
  const linesPts =
    meta.claimLines && meta.claimLines > 0
      ? Math.min(30, Math.round(6 * Math.log10(meta.claimLines + 1)))
      : 0
  const complexityPts = { S: 20, M: 12, L: 5 }[meta.complexity ?? 'L'] ?? 0
  return Math.min(100, valuePts + linesPts + complexityPts)
}

export const canTransition = (
  from: InitiativeStage,
  to: InitiativeStage,
): boolean => STAGE_TRANSITIONS[from].includes(to)

/* Per-key validators for fields whose default is `null` or a picklist. */
const FIELD_GUARDS: Record<string, (value: unknown) => boolean> = {
  stage: (value) => INITIATIVE_STAGES.includes(value as InitiativeStage),
  stream: (value) => INITIATIVE_STREAMS.includes(value as InitiativeStream),
  intakeSource: (value) => INTAKE_SOURCES.includes(value as IntakeSource),
  complexity: (value) =>
    value === null || COMPLEXITIES.includes(value as Complexity),
  estValue: (value) =>
    value === null || (typeof value === 'number' && Number.isFinite(value)),
  claimLines: (value) =>
    value === null || (typeof value === 'number' && Number.isFinite(value)),
}

/**
 * Overlays stored fields on the defaults. A missing or wrong-typed value falls
 * back to the default, because clients can write these maps.
 */
const pick = <T extends object>(defaults: T, raw: Record<string, unknown>): T =>
  Object.fromEntries(
    Object.entries(defaults).map(([key, fallback]) => {
      const value = raw[key]
      const guard =
        FIELD_GUARDS[key] ??
        ((candidate: unknown) => typeof candidate === typeof fallback)
      return [key, value !== undefined && guard(value) ? value : fallback]
    }),
  ) as T

const hasStringFields = (value: unknown, fields: readonly string[]): boolean =>
  typeof value === 'object' &&
  value !== null &&
  fields.every(
    (field) => field in value && typeof Reflect.get(value, field) === 'string',
  )

const isBuildTask = (value: unknown): value is BuildTask =>
  hasStringFields(value, ['id', 'title', 'detail', 'owner', 'estimate'])

export const isActivityEvent = (value: unknown): value is ActivityEvent =>
  hasStringFields(value, ['id', 'at', 'actor', 'text', 'icon'])

/** Reads every container into plain data, tolerating a not-yet-seeded doc. */
export const readInitiativeDoc = (doc: LoroDoc): InitiativeDocView => ({
  meta: pick(
    DEFAULT_META,
    doc.getMap(INITIATIVE_DOC.meta).toJSON() as Record<string, unknown>,
  ),
  scope: pick(
    DEFAULT_SCOPE,
    doc.getMap(INITIATIVE_DOC.scope).toJSON() as Record<string, unknown>,
  ),
  procedureCodes: doc
    .getMovableList(INITIATIVE_DOC.procedureCodes)
    .toArray()
    .filter((code): code is string => typeof code === 'string'),
  claimTypes: doc
    .getMovableList(INITIATIVE_DOC.claimTypes)
    .toArray()
    .filter((claimType): claimType is string => typeof claimType === 'string'),
  insightLogic: doc.getText(INITIATIVE_DOC.insightLogic).toString(),
  /* Tasks are client-writable, so a malformed entry is reachable: drop
     anything that is not shaped like a task rather than trusting a cast. */
  tasks: doc.getMovableList(INITIATIVE_DOC.tasks).toArray().filter(isBuildTask),
  /* Mirrored from server-owned storage, but read defensively all the same. */
  activity: doc
    .getList(INITIATIVE_DOC.activity)
    .toArray()
    .filter(isActivityEvent),
})

export const summarize = (
  view: InitiativeDocView,
  updatedAt: string,
): InitiativeSummary => {
  const checklist = scopeChecklist(view)
  return {
    id: view.meta.id,
    title: view.meta.title,
    stage: view.meta.stage,
    stream: view.meta.stream,
    intakeSource: view.meta.intakeSource,
    connector: view.meta.connector,
    topic: view.meta.topic,
    owner: view.meta.owner,
    referenceSource: view.scope.referenceSource,
    estValue: view.meta.estValue,
    claimLines: view.meta.claimLines,
    complexity: view.meta.complexity,
    score: scoreInitiative(view.meta),
    scopeComplete: checklist.filter((item) => item.done).length,
    scopeTotal: checklist.length,
    tasksGenerated: view.tasks.length > 0,
    createdAt: view.meta.createdAt,
    stageChangedAt: view.meta.stageChangedAt,
    updatedAt,
  }
}

/**
 * Deterministic build-task breakdown from the scope record — the MVP stand-in
 * for a model-drafted plan, so the handoff round trip is provable without an
 * API key. Owners are left blank for the content lead to assign.
 */
export const buildTasksFromScope = (view: InitiativeDocView): BuildTask[] => {
  const codes = view.procedureCodes.length
  return [
    {
      title: `Define predicate: ${view.scope.editType || 'edit'} code set`,
      detail: `${codes} procedure code${codes === 1 ? '' : 's'} from scope`,
      estimate: codes > 5 ? '5 pts' : '3 pts',
    },
    {
      title: 'Configure claim type routing',
      detail: view.claimTypes.join(', ') || 'Claim types not yet set',
      estimate: '1 pt',
    },
    {
      title: 'Author insight message and client-facing description',
      detail: 'Uses insight logic text from scope',
      estimate: '2 pts',
    },
    {
      title: 'Build test plan',
      detail: 'Positive and negative claim scenarios per predicate',
      estimate: '5 pts',
    },
    {
      title: 'Content peer review',
      detail: 'Second content analyst sign-off',
      estimate: '2 pts',
    },
  ].map((task, index) => ({ ...task, id: `task-${index + 1}`, owner: '' }))
}
